import React, { useState, useEffect, useMemo, Fragment, useRef } from 'react';
import { getSupabase } from '../lib/supabase.js';
import { shapeSchedule, scheduleCopy, equationShare } from '../lib/payoutSchedule.js';

import './settlements.css';

// Stub out the tweaks panel — designer-only tool, no production presence.
const TweaksPanel = ({ children }) => null;
const TweakSection = ({ children }) => null;
const TweakRadio = () => null;
const TweakSelect = () => null;
const TweakColor = () => null;
const TweakSlider = () => null;
const TweakText = () => null;
const TweakToggle = () => null;
const TweakButton = () => null;
const useTweaks = (defaults) => [defaults, () => {}];

// settlements.jsx — settlement history page for Mahmoud's investment ledger.
// Tweakable demo state: empty (consistent with home: 0 settlements, $1,920 outstanding)
// vs populated (3 mock entries; populated state assumes a longer-running ledger).



const METHODS = {
  mercury:  { name: 'Mercury',  sub: 'ACH',        letter: 'M', bg: '#0f172a', fg: '#fff' },
  zelle:    { name: 'Zelle',    sub: 'Bank-to-bank',letter: 'Z', bg: '#6d28d9', fg: '#fff' },
  wire:     { name: 'Wire',     sub: 'Same-day',   letter: 'W', bg: '#1d4ed8', fg: '#fff' },
  coinbase: { name: 'Coinbase', sub: 'USDC',       letter: 'C', bg: '#1e40af', fg: '#fff' },
  other:    { name: 'Other',    sub: 'Custom',     letter: '+', bg: '#f3f4fa', fg: '#475569' },
};

// Populated demo data — internally consistent: 2 confirmed ($1,000 total) +
// 1 pending ($1,920). Lifetime value-generated to Kareem would be $2,920;
// confirmed-settled $1,000; outstanding $1,920 (matches home).
const DEMO_SETTLEMENTS = [
  {
    id: 'SETL-2026-0514-A4F2',
    date: '2026-05-14',
    dateLabel: 'May 14, 2026',
    dateSub: '3:42 PM PT',
    amount: 1920,
    method: 'mercury',
    status: 'pending',
    memo: 'Cycle 4 settle — Mercury ACH expected to land May 16',
  },
  {
    id: 'SETL-2026-0415-7B3C',
    date: '2026-04-15',
    dateLabel: 'Apr 15, 2026',
    dateSub: '10:18 AM PT',
    amount: 580,
    method: 'zelle',
    status: 'confirmed',
    confirmedAt: 'Apr 15, 11:02 AM',
    memo: 'Monthly · cycle 3',
  },
  {
    id: 'SETL-2026-0315-2E1A',
    date: '2026-03-15',
    dateLabel: 'Mar 15, 2026',
    dateSub: '7:55 AM PT',
    amount: 420,
    method: 'coinbase',
    status: 'confirmed',
    confirmedAt: 'Mar 15, 8:04 AM',
    memo: '',
  },
];

const fmt$ = (n) => '$' + Number(n).toLocaleString('en-US');
// T-039c: exact-cents formatter for the who-owes-whom equation — the terms
// must visibly sum to the result, so they render unrounded.
const fmtC = (n) => '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ─── Chrome ───


// ─── Settlement row ───
const SettlementRow = ({ s, onEdit, onDelete }) => {
  // Fall back to the generic "other" tile if the payment_method string
  // doesn't map to one of the known METHODS keys.
  const m = METHODS[s.method] || METHODS.other;
  const methodName = m.name === 'Other' ? (s.methodName || 'Other') : m.name;
  const isPending = s.status === 'pending';
  // D-9: a payout row is Kareem paying YOU — green, marked, and none of the
  // investor actions apply (it's his record; the DB refuses tampering too).
  const isPayout = s.direction === 'to_investor';
  return (
    <tr>
      <td>
        <div className="st-date">{s.dateLabel}</div>
        <div className="st-date-sub">{s.dateSub}</div>
      </td>
      <td>
        <div className="st-ref">{s.id}</div>
      </td>
      <td>
        <div className="st-method">
          <div className="st-method-tile" style={isPayout ? { background: 'var(--green-bg, #f0fdf4)', color: 'var(--green, #059669)' } : { background: m.bg, color: m.fg }}>{isPayout ? '←' : m.letter}</div>
          <div>
            <div className="st-method-name">{isPayout ? (s.methodName || 'Payout') : methodName}</div>
            <div className="st-method-sub" style={isPayout ? { color: 'var(--green, #059669)', fontWeight: 700 } : undefined}>{isPayout ? 'Kareem → you' : m.sub}</div>
          </div>
        </div>
      </td>
      <td>
        <span className={'st-status ' + s.status}>
          <span className="dot"></span>
          {s.status}
        </span>
        {s.confirmedAt && (
          <div className="st-date-sub" style={{ marginTop: 4 }}>
            confirmed {s.confirmedAt}
          </div>
        )}
      </td>
      <td>
        {s.memo
          ? <div className="st-memo">{s.memo}</div>
          : <div className="st-memo is-empty">no memo</div>}
      </td>
      <td>
        <div className="st-amt" style={isPayout ? { color: 'var(--green, #059669)' } : undefined}>{isPayout ? '−' : ''}{fmt$(s.amount)}</div>
      </td>
      <td className="st-actions">
        {isPayout ? (
          <span className="st-action-locked" title="Booked by Kareem — a payout is his record; ask him to adjust it">By Kareem</span>
        ) : isPending ? (
          <>
            <button onClick={() => onEdit && onEdit(s)} title="Edit this pending settlement">Edit</button>
            <button className="st-action-danger" onClick={() => onDelete && onDelete(s)} title="Delete this pending settlement">Delete</button>
          </>
        ) : s.status === 'confirmed' ? (
          /* T-023: editing a confirmed payment is a REQUEST — it goes back to
             pending with the old values preserved, and Kareem re-confirms
             (or rejects) from his queue. */
          <button onClick={() => onEdit && onEdit(s)} title="Request an edit — the payment returns to pending for Kareem to confirm or reject">Request edit</button>
        ) : (
          <span className="st-action-locked" title="Rejected by Kareem — see the memo for why">Rejected</span>
        )}
      </td>
    </tr>
  );
};

// ─── Empty state ───
const EmptyState = () => (
  <div className="empty-state">
    <div className="empty-art">
      <svg viewBox="0 0 32 32" width="38" height="38" fill="none">
        <path d="M6 10h20M6 16h14M6 22h20" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
        <circle cx="24" cy="22" r="3.5" stroke="currentColor" strokeWidth="2.2" fill="#fff" />
      </svg>
    </div>
    <h3 className="empty-title">No settlements yet</h3>
    <p className="empty-sub">
      You enrolled <strong style={{ color: 'var(--text-1)' }}>May 13</strong>, and Kareem holds <strong style={{ color: 'var(--text-1)' }}>$1,920</strong> in
      outstanding claims against your ledger. Settle now or wait until the monthly cadence on <strong style={{ color: 'var(--text-1)' }}>June 13</strong>.
    </p>
    <div className="empty-actions">
      <a className="btn-primary" href="Settle Now.html">
        Settle now · $1,920
      </a>
      <button className="btn-ghost">
        Change cadence
      </button>
    </div>

    <div className="empty-divider"></div>
    <div className="empty-preview-label">Once you settle, rows will look like this</div>
    <div className="empty-preview-row">
      <div>
        <div style={{ fontSize: 12.5, color: 'var(--text-2)', fontWeight: 600 }}>Jun 13</div>
        <div style={{ fontSize: 10.5, color: 'var(--text-3)' }}>est. next</div>
      </div>
      <div className="st-ref">SETL-2026-0613-····</div>
      <div className="st-method">
        <div className="st-method-tile" style={{ background: '#e5e7ef', color: '#94a3b8' }}>?</div>
        <div className="st-method-sub">method TBD</div>
      </div>
      <span className="st-status" style={{ background: 'var(--surface)', color: 'var(--text-3)', border: '1px dashed var(--border-2)' }}>
        <span className="dot" style={{ background: 'var(--text-3)' }}></span>
        upcoming
      </span>
      <div className="mono" style={{ fontWeight: 700, color: 'var(--text-3)', textAlign: 'right' }}>~$510</div>
    </div>
    <div className="empty-preview-caption">
      Projected from current cycle velocity (~$510 / monthly cycle)
    </div>
  </div>
);

// ─── App ───
function Settlements({ member } = {}) {
  const [tweaks, setTweak] = useState({ filterStatus: 'all' });
  const [rows, setRows] = useState([]);
  const [outstanding, setOutstanding] = useState(0);
  const [hurdleRecovered, setHurdleRecovered] = useState(0);
  const [hurdleTotal, setHurdleTotal] = useState(0);
  const [credit, setCredit] = useState(0); // D5/D21: Kareem owes the household
  const [S, setS] = useState(null); // full contract — the numeric credit explainer reads it
  const [schedule, setSchedule] = useState(null); // when Kareem's credit is paid (v2_settlement_schedule)
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  // Edit/delete state — pending settlements only. `editing` is the row
  // object being edited (or null); `deleting` is the row pending a
  // delete confirmation (or null).
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const sb = (typeof window !== 'undefined') ? window.sb : null;
      if (!sb || !member?.id) { setLoading(false); return; }
      setLoading(true);
      try {
        const [settles, events, enrollment, subs] = await Promise.all([
          sb.from('investment_settlements').select('*').eq('enrolled_member_id', member.id).order('settle_date', { ascending: false }),
          sb.from('value_events').select('kareem_share').eq('enrolled_member_id', member.id).is('voided_at', null),
          sb.from('module_enrollment').select('config').eq('member_id', member.id).eq('module_name', 'investment').eq('enabled', true).maybeSingle(),
          sb.from('loop_subscriptions').select('id').eq('enrolled_member_id', member.id).eq('active', true),
        ]);
        if (cancelled) return;
        // Settlements carry pending/confirmed status from the DB. Pending
        // submissions await Kareem's confirmation before reducing outstanding.
        const mapped = (settles.data || []).map((r) => ({
          id: r.id,
          settleDate: r.settle_date || '',
          dateLabel: r.settle_date ? new Date(r.settle_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '',
          amount: Number(r.amount || 0),
          paymentMethod: r.payment_method || '',
          method: (r.payment_method || 'other').toLowerCase().split(' ')[0],
          methodName: r.payment_method || 'Other',
          status: r.status || 'pending',
          // T-039: kind must survive the map — the cash-only stats filter on it
          // (a confirmed waiver is a settlement row but never money received).
          kind: r.kind || 'cash',
          // D-9: direction must survive too — a payout row is Kareem paying
          // YOU, never one of your own payments.
          direction: r.direction || 'to_kareem',
          memo: r.memo || '',
        }));
        // Contract-first (Release 5 / T-022): the summary RPC is the one
        // authoritative money math (D4 waterfall). The split-share
        // computation below survives only as the RPC-unreachable fallback.
        let contract = null;
        try {
          const { data: s, error: sErr } = await sb.rpc('v2_household_summary', { p_member_id: member.id });
          if (!sErr && s && typeof s === 'object' && s.holder) contract = s;
        } catch { /* legacy fallback */ }

        // Payout schedule: only adds a "when" — tolerant, a failure just
        // leaves the cadence tile generic.
        let sched = null;
        if (contract) {
          try {
            const { data: d, error: dErr } = await sb.rpc('v2_settlement_schedule', { p_member_id: member.id });
            if (!dErr && d && typeof d === 'object' && d.holder) sched = shapeSchedule(d);
          } catch { /* schedule stays hidden */ }
        }
        if (cancelled) return;

        if (contract) {
          setRows(mapped);
          setS(contract);
          setSchedule(sched);
          setOutstanding(Math.round(Number(contract.outstanding || 0)));
          setCredit(Math.round(Number(contract.credit || 0)));
          setHurdleRecovered(Math.round(Number(contract.hurdleProgress || 0)));
          setHurdleTotal(Number(contract.hurdleTarget || 0));
          setLoading(false);
          return;
        }

        // D-15 Session C: the meter fallback is retired — no contract, no
        // numbers (T-034). The rows still render; the money stats show the
        // failure state instead of a custody-blind figure.
        setRows(mapped);
        setLoadError('The summary service could not be reached — money figures cannot be shown right now.');
        setLoading(false);
      } catch (e) {
        if (!cancelled) { setLoadError(e?.message || String(e)); setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [member?.id, reloadKey]);

  // Save edits to a pending settlement (amount, date, method, memo).
  // Confirmed rows can't reach this handler — UI doesn't expose Edit on them.
  const saveEdit = async (patch) => {
    const sb = getSupabase();
    if (!sb || !editing) return;
    const amt = Number(patch.amount);
    if (!Number.isFinite(amt) || amt <= 0) { alert('Enter a positive amount.'); return; }
    if (editing.status === 'confirmed') {
      // T-023: confirmed rows change only through the edit-request RPC —
      // the payment returns to pending (old values on the memo) and lands
      // in Kareem's queue for Confirm / Reject.
      const { error } = await sb.rpc('v1_request_payment_edit', {
        p_payment: editing.id,
        p_amount: amt,
        p_date: patch.settleDate,
        p_method: patch.paymentMethod?.trim() || null,
        p_memo: patch.memo?.trim() || null,
      });
      if (error) { alert('Edit request failed: ' + (error.message || error)); return; }
      alert('Edit requested — the payment is back in Kareem\'s pending queue for confirmation.');
    } else {
      const { error } = await sb.from('investment_settlements').update({
        amount: amt,
        settle_date: patch.settleDate,
        payment_method: patch.paymentMethod?.trim() || null,
        memo: patch.memo?.trim() || null,
      }).eq('id', editing.id);
      if (error) { alert('Save failed: ' + (error.message || error)); return; }
    }
    setEditing(null);
    setReloadKey((k) => k + 1);
    if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('finops:enrollment-changed'));
  };

  // Delete a pending settlement. Confirmed rows can't reach this handler.
  const performDelete = async () => {
    const sb = getSupabase();
    if (!sb || !deleting) return;
    const { error } = await sb.from('investment_settlements').delete().eq('id', deleting.id);
    if (error) { alert('Delete failed: ' + (error.message || error)); return; }
    setDeleting(null);
    setReloadKey((k) => k + 1);
    if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('finops:enrollment-changed'));
  };

  const populated = rows.length > 0;
  const visibleRows = useMemo(() => {
    if (tweaks.filterStatus === 'all') return rows;
    return rows.filter(r => r.status === tweaks.filterStatus);
  }, [rows, tweaks.filterStatus]);

  // T-039 (D18 discipline): a confirmed WAIVER is a settlement row but never
  // cash — "Lifetime settled" must count money that actually moved.
  // D-9: and it counts YOUR payments only — Kareem's payouts (to_investor)
  // are the reversed direction, totaled separately.
  const cashRows        = rows.filter(r => r.kind !== 'waiver' && r.direction !== 'to_investor');
  const lifetimeSettled = cashRows.filter(r => r.status === 'confirmed').reduce((s, r) => s + r.amount, 0);
  const pendingTotal    = cashRows.filter(r => r.status === 'pending').reduce((s, r) => s + r.amount, 0);
  const paidOutTotal    = rows.filter(r => r.direction === 'to_investor' && r.status === 'confirmed')
                              .reduce((s, r) => s + r.amount, 0);
  const settlementCount = rows.length;
  // Past the hurdle the equation needs the investor's own share to sum.
  const share = S ? equationShare({
    investorNet: S.investorNet, rewards: S.totalValue,
    costs: Number(S.recognizedCosts || 0) - Number(S.kareemPaidCosts || 0),
    held: S.poolHeldValue, paidIn: S.paidByInvestor ?? S.confirmedPayments, paidOut: S.paidToInvestor,
    waived: S.waivers, result: -Number(S.credit || 0),
  }) : 0;
  const payout = credit > 0 ? scheduleCopy(schedule, (n) => fmt$(Math.round(n))) : null;
  const hurdlePct = hurdleTotal > 0 ? Math.min(1, hurdleRecovered / hurdleTotal) : 0;
  const hurdlePctLabel = (hurdlePct * 100).toFixed(1).replace(/\.0$/, '');

  if (loading) {
    return <div><main className="page"><div className="stat" style={{ color: 'var(--text-2)' }}>Loading settlements…</div></main></div>;
  }
  if (loadError) {
    return <div><main className="page"><div className="stat" style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#9f1239' }}><strong>Couldn't load settlements.</strong> {loadError}</div></main></div>;
  }

  return (
    <div>
      
      
      <main className="page">
        <div className="breadcrumb">
          <a href="Investment Home.html">Investment</a>
          <span>›</span>
          <span className="here">Settlements</span>
        </div>

        <div className="title-row">
          <div>
            <div className="eyebrow">Ledger with Kareem · settlements log</div>
            <h1 className="title">Settlements</h1>
            <p className="sub">
              Every payment you've made against Kareem's outstanding claim. Settlements pay down the ledger but never affect hurdle progress —
              that math is set by value events alone.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <a className="btn-ghost" href="#home">← Back</a>
            <button className="btn-ghost">Export CSV</button>
            <a className="btn-primary" href="Settle Now.html">
              Settle now
              <span>→</span>
            </a>
          </div>
        </div>

        {/* Stats */}
        <div className="stats-row">
          <div className="stat">
            <span className="stat-lbl">Lifetime settled</span>
            <span className="stat-val mono">{fmt$(lifetimeSettled)}</span>
            <span className="stat-sub">across {cashRows.filter(r => r.status === 'confirmed').length} confirmed payments{paidOutTotal > 0 && <> · <span style={{ color: 'var(--green)', fontWeight: 700 }}>Kareem paid you {fmt$(paidOutTotal)}</span></>}</span>
          </div>
          {/* T-039b: the reversed direction POPS (green tile) and the sub shows
              the actual equation, not just the logic. D-9: the cash terms are
              directional — "− paid" (yours in) and "+ paid back" (his out) —
              because the net goes negative after a payout and stops summing. */}
          <div className={'stat ' + (credit > 0 ? 'is-credit' : 'is-outstanding')}>
            <span className="stat-lbl">{credit > 0 ? 'Kareem owes you' : 'Outstanding to Kareem'}</span>
            <span className="stat-val mono">{fmt$(credit > 0 ? credit : outstanding)}</span>
            <span className="stat-sub">{credit > 0
              ? (S
                  ? <>rewards {fmtC(S.totalValue)} − costs {fmtC(Number(S.recognizedCosts || 0) - Number(S.kareemPaidCosts || 0))}{share > 0 ? <> − your share {fmtC(share)}</> : null} − Kareem holds {fmtC(S.poolHeldValue)}{Number(S.paidByInvestor ?? S.confirmedPayments ?? 0) > 0 ? <> − paid {fmtC(S.paidByInvestor ?? S.confirmedPayments)}</> : null}{Number(S.paidToInvestor || 0) > 0 ? <> + paid back {fmtC(S.paidToInvestor)}</> : null}{Number(S.waivers || 0) > 0 ? <> − waived {fmtC(S.waivers)}</> : null} = −{fmtC(S.credit)}</>
                  : 'waivers, payments and what Kareem already holds exceed the rewards')
              : 'rewards minus costs, what Kareem holds, payments, and waivers'}</span>
          </div>
          <div className="stat">
            <span className="stat-lbl">Pending confirmation</span>
            <span className="stat-val mono">{pendingTotal > 0 ? fmt$(pendingTotal) : '—'}</span>
            <span className="stat-sub">{pendingTotal > 0 ? 'awaiting Kareem' : 'nothing in flight'}</span>
          </div>
          {/* Owner, 2026-10-19: each month's balance is paid by the end of
              the next — so the tile says when Kareem's credit reaches you. */}
          {payout ? (
            <div className={'stat is-payout' + (payout.overdue ? ' is-overdue' : '')}>
              <span className="stat-lbl">{payout.label}</span>
              <span className="stat-val mono">{fmt$(Math.round(payout.amount))}</span>
              <span className="stat-sub">{payout.sub}</span>
            </div>
          ) : (
            <div className="stat is-cadence">
              <span className="stat-lbl">Cadence</span>
              <span className="stat-val">Monthly</span>
              <span className="stat-sub">each month's balance settles by the end of the next</span>
            </div>
          )}
        </div>

        {/* Outstanding hero */}
        <section className="outstanding-hero">
          <div className="oh-main">
            <div className="oh-eyebrow">
              <span className="dot"></span>
              Ready to settle
            </div>
            <div className="oh-amt">
              <span className="oh-cur">$</span>
              <span className="oh-num">{outstanding.toLocaleString()}</span>
            </div>
            <p className="oh-sub">
              {outstanding > 0
                ? <>You owe Kareem {fmt$(outstanding)} in value-event share. He's expecting it on the monthly cadence, but settling early or in partial chunks is fine — the ledger just tracks net.</>
                : <>Nothing outstanding. Once value events fire and Kareem's share accrues, it'll show up here.</>}
            </p>
            <div className="oh-actions">
              {outstanding > 0 && (
                <a className="btn-primary" href="Settle Now.html">
                  Settle {fmt$(outstanding)} now
                  <span>→</span>
                </a>
              )}
              <button className="btn-ghost">Settle partial amount</button>
            </div>
          </div>
          <div className="oh-side">
            <div className="oh-mini">
              <div className="oh-mini-lbl">Hurdle progress</div>
              <div className="oh-mini-val">{fmt$(hurdleRecovered)} / {fmt$(hurdleTotal)}</div>
              <div className="oh-mini-sub">{hurdlePctLabel}% · unaffected by settlements</div>
            </div>
            <div className="oh-mini">
              <div className="oh-mini-lbl">Value events YTD</div>
              <div className="oh-mini-val">$1,920</div>
              <div className="oh-mini-sub">4 cycles complete · ~$510/cycle</div>
            </div>
            <div className="oh-mini">
              <div className="oh-mini-lbl">After this settlement</div>
              <div className="oh-mini-val" style={{ color: '#6ee7b7' }}>$0 outstanding</div>
              <div className="oh-mini-sub">slate clear · hurdle still 19%</div>
            </div>
          </div>
        </section>

        {/* Settlements list */}
        <section className="card">
          <header className="card-header">
            <div>
              <div className="card-eyebrow">Append-only ledger</div>
              <h2 className="card-title">Settlement history</h2>
              <p className="card-desc">
                {populated
                  ? `${settlementCount} settlement${settlementCount === 1 ? '' : 's'} logged · ${fmt$(lifetimeSettled)} confirmed`
                  : 'Your first settlement will appear here'}
              </p>
            </div>
            {populated && (
              <div className="filter-row">
                {['all', 'confirmed', 'pending', 'voided'].map(f => (
                  <button
                    key={f}
                    className={'filter-pill' + (tweaks.filterStatus === f ? ' active' : '')}
                    onClick={() => setTweak('filterStatus', f)}
                  >
                    {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
                  </button>
                ))}
              </div>
            )}
          </header>

          {populated ? (
            <div style={{ overflowX: 'auto' }}>
              <table className="settle-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Reference</th>
                    <th>Method</th>
                    <th>Status</th>
                    <th>Memo</th>
                    <th style={{ textAlign: 'right' }}>Amount</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.length === 0 ? (
                    <tr>
                      <td colSpan={7} style={{ textAlign: 'center', padding: 32, color: 'var(--text-3)' }}>
                        No settlements match this filter.
                      </td>
                    </tr>
                  ) : (
                    visibleRows.map(s => <SettlementRow key={s.id} s={s} onEdit={setEditing} onDelete={setDeleting} />)
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState />
          )}
        </section>

        {/* Footer cards */}
        <div className="footer-grid">
          <section className="card">
            <header className="card-header">
              <div>
                <div className="card-eyebrow">Reference</div>
                <h2 className="card-title">How settlements work</h2>
              </div>
            </header>
            <ol className="how-list">
              <li className="how-item">
                <div className="how-num">1</div>
                <div className="how-text">
                  <strong>Value events accrue to Kareem's claim.</strong>{' '}
                  Each money movement that matches an active step in a subscribed loop generates value;
                  during the hurdle phase, 100% goes to Kareem's outstanding balance.
                </div>
              </li>
              <li className="how-item">
                <div className="how-num">2</div>
                <div className="how-text">
                  <strong>You settle on your cadence.</strong>{' '}
                  Monthly is the default — the bot nudges around the 15th. Settle anytime via Mercury, Zelle, wire, or Coinbase USDC.
                </div>
              </li>
              <li className="how-item">
                <div className="how-num">3</div>
                <div className="how-text">
                  <strong>Kareem confirms; ledger zeroes.</strong>{' '}
                  Pending settlements appear immediately; outstanding drops to $0 once he marks the payment received.
                </div>
              </li>
              <li className="how-item">
                <div className="how-num">4</div>
                <div className="how-text">
                  <strong>Hurdle progress is independent.</strong>{' '}
                  Settling doesn't reset hurdle math — it tracks lifetime value generated, not what you owe today.
                </div>
              </li>
            </ol>
          </section>

          <section className="card">
            <header className="card-header">
              <div>
                <div className="card-eyebrow">Payment methods</div>
                <h2 className="card-title">Where to send</h2>
              </div>
            </header>
            <div className="methods-grid">
              {Object.entries(METHODS).filter(([k]) => k !== 'other').map(([k, m]) => (
                <div key={k} className="method-mini">
                  <div className="method-mini-tile" style={{ background: m.bg, color: m.fg }}>{m.letter}</div>
                  <div>
                    <div className="method-mini-name">{m.name}</div>
                    <div className="method-mini-sub">{m.sub}</div>
                  </div>
                </div>
              ))}
              <div className="method-mini" style={{ gridColumn: 'span 2', justifyContent: 'center', borderStyle: 'dashed', background: 'var(--surface)', color: 'var(--text-3)' }}>
                <span style={{ fontSize: 11.5, fontWeight: 600 }}>+ Other / custom (note where it went)</span>
              </div>
            </div>
            <div style={{ marginTop: 14, padding: '10px 12px', background: 'var(--surface-2)', borderRadius: 10, fontSize: 11.5, color: 'var(--text-2)', lineHeight: 1.5 }}>
              <strong style={{ color: 'var(--text-1)' }}>Kareem's USDC address:</strong>{' '}
              <span className="mono" style={{ background: '#fff', padding: '2px 6px', borderRadius: 4, fontSize: 11 }}>0x4b…f9c2</span>
              {' '}— see Settle now for full payment instructions.
            </div>
          </section>
        </div>
      </main>

      {editing && (
        <EditSettlementModal
          settlement={editing}
          onCancel={() => setEditing(null)}
          onSave={saveEdit}
        />
      )}

      {deleting && (
        <DeleteSettlementConfirm
          settlement={deleting}
          onCancel={() => setDeleting(null)}
          onConfirm={performDelete}
        />
      )}
    </div>
  );
}

// Inline modal for editing a pending settlement. Confirmed rows never
// reach this — the parent only opens it for status === 'pending'.
function EditSettlementModal({ settlement, onCancel, onSave }) {
  const [amount, setAmount] = useState(String(settlement.amount || ''));
  const [settleDate, setSettleDate] = useState(settlement.settleDate || new Date().toISOString().slice(0, 10));
  const [paymentMethod, setPaymentMethod] = useState(settlement.paymentMethod || '');
  const [memo, setMemo] = useState(settlement.memo || '');
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onSave({ amount, settleDate, paymentMethod, memo });
    setSaving(false);
  };
  return (
    <div className="settle-modal-backdrop" onClick={onCancel} role="presentation">
      <form className="settle-modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="settle-modal-head">
          <div>
            <div className="settle-modal-eyebrow">Pending settlement</div>
            <h3 className="settle-modal-title">Edit settlement</h3>
          </div>
          <button type="button" aria-label="Close" className="settle-modal-close" onClick={onCancel}>✕</button>
        </div>
        <label className="settle-modal-field">
          <span>Amount ($)</span>
          <input onWheel={(e) => e.currentTarget.blur()} type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} disabled={saving} required autoFocus />
        </label>
        <label className="settle-modal-field">
          <span>Settle date</span>
          <input type="date" value={settleDate} onChange={(e) => setSettleDate(e.target.value)} disabled={saving} required />
        </label>
        <label className="settle-modal-field">
          <span>Payment method</span>
          <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} disabled={saving}>
            <option value="">— select —</option>
            <option value="Mercury">Mercury</option>
            <option value="Zelle">Zelle</option>
            <option value="Wire">Wire</option>
            <option value="Coinbase">Coinbase</option>
            <option value="Other">Other</option>
          </select>
        </label>
        <label className="settle-modal-field">
          <span>Memo (optional)</span>
          <textarea rows={2} value={memo} onChange={(e) => setMemo(e.target.value)} disabled={saving} placeholder="What this payment covers" />
        </label>
        <div className="settle-modal-actions">
          <button type="button" className="btn-ghost" onClick={onCancel} disabled={saving}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button>
        </div>
      </form>
    </div>
  );
}

function DeleteSettlementConfirm({ settlement, onCancel, onConfirm }) {
  const [working, setWorking] = useState(false);
  return (
    <div className="settle-modal-backdrop" onClick={onCancel} role="presentation">
      <div className="settle-modal settle-modal-danger" onClick={(e) => e.stopPropagation()} role="alertdialog" aria-labelledby="del-settle-title">
        <div className="settle-modal-head">
          <div>
            <div className="settle-modal-eyebrow" style={{ color: 'var(--rose, #e11d48)' }}>Delete pending settlement</div>
            <h3 id="del-settle-title" className="settle-modal-title">Are you sure?</h3>
          </div>
          <button type="button" aria-label="Close" className="settle-modal-close" onClick={onCancel}>✕</button>
        </div>
        <p style={{ margin: '0 0 14px', color: 'var(--text-2, #475569)', fontSize: 13.5, lineHeight: 1.5 }}>
          You'll remove this <strong>{fmt$(settlement.amount)}</strong> settlement
          ({settlement.methodName || 'Other'} · {settlement.dateLabel}). Kareem won't see it anymore.
          This can't be undone — but you can always log a fresh settlement.
        </p>
        <div className="settle-modal-actions">
          <button type="button" className="btn-ghost" onClick={onCancel} disabled={working}>Cancel</button>
          <button
            type="button"
            className="btn-primary settle-modal-danger-btn"
            disabled={working}
            onClick={async () => { setWorking(true); await onConfirm(); setWorking(false); }}
          >{working ? 'Deleting…' : 'Yes, delete it'}</button>
        </div>
      </div>
    </div>
  );
}

export default Settlements;
